import type { FC } from 'react';
import React, { memo } from 'react';
import { Link } from '@compat/router';
import {
  DeliveryFastIcon,
  ExpressIcon,
  FreezeIcon,
  HoveredButton,
  Picture,
  Tooltip,
  Popover
} from '@ui';
import icons from '@icons';
import { declension } from '@utils/declension';
import SubscribeProduct from '@components/SubscribeProduct';
import InfoModal from '@components/Modals/InfoModal';
import './components.scss';
import type { IconType } from '@root/src/types';
import type { ProductInfoI } from '../index.type';
import { twcls } from '@utils/twcls';
import type { Badge } from '@utils/prepareBadges';
import useClipboard from '@customHooks/useClipboard';
import ShareSocialLinksNew from '@components/ShareSocialLinksNew';
import clsx from 'clsx';

const {
  chevronRight: {
    black: chevronRightBlackOutlined,
    darkGrey: chevronRightDGOutlined
  },
  bell: { black: bellBlackOutlined },
  at: { black: atBlackOutlined },
  flip2: { black: flip2BlackOutlined },
  // info: { black: infoBlackOutlined },
  share: { black: shareBlackOutlined }
} = icons.Outline;

const {
  qmCircle: { darkGrey: qmCircleDGFilled },
  cart: { darkGrey: cartDGFilled },
  scale: { black: scaleBlackFilled },
  car: { orange: carOrangeFilled, black: carBlackFilled },
  star: { yellow: starYellowFill, grey: starGeryFilled },
  calendar: { black: calendarBlackFilled },
  share: { red: shareRedFilled },
  rocket: { purple: rocketPurpleFilled }
} = icons.Fill;

interface TotalRatingProps {
  rating: number;
}

export const TotalRating: FC<TotalRatingProps> = memo(({ rating }) => (
  <div className='pp-rating'>
    <Picture
      icon={!rating ? starGeryFilled : starYellowFill}
      width='24'
      height='24'
      alt='звезда'
    />
    {rating}
  </div>
));

interface SupplierProps {
  supplier_id: string;
}

export const Supplier: FC<SupplierProps> = memo(({ supplier_id }) => (
  <Link className='pp-supplier' to={`/supplier/${supplier_id}`}>
    Все товары поставщика
    <Picture icon={chevronRightBlackOutlined} width='20' height='20' />
  </Link>
));

interface TitleProps {
  title: string;
  subtitle?: string;
  article?: string;
  badges?: Badge[];
}

export const Title: FC<TitleProps> = memo(
  ({ title, subtitle, article, badges }) => {
    const { copyPromocode: onVendorCodeClick } = useClipboard({
      text: article,
      notificationText: `Артикул ${article} скопирован!`
    });
    return (
      <div className='mb-6'>
        <h1 className='pp-title'>{title}</h1>
        {/* subtitle */}
        {(subtitle || article || !!badges?.length) && (
          <div className='inline-block'>
            {(subtitle || article) && (
              <p className='pp-subtitle'>
                {subtitle ? `${subtitle}${article ? ',' : ''}\u00A0` : ''}
                {article && (
                  <span className='mr-2'>
                    артикул&nbsp;
                    <span
                      className=' cursor-pointer transition-colors hover:text-[var(--dark-red)]'
                      onClick={onVendorCodeClick}
                    >
                      {article}
                    </span>
                  </span>
                )}
              </p>
            )}
            {!!badges?.length && (
              <div className='inline-flex gap-x-1 sm:text-sm'>
                {badges.map(item => (
                  <div key={item.id} style={{ color: item.color }}>
                    {item.title}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);

interface NewBadgesProps {
  newBadges: Badge[];
}

export const NewBadges: FC<NewBadgesProps> = memo(({ newBadges }) => (
  <div className='pp-badges'>
    {newBadges.map(item => (
      <div key={item.id} style={{ color: item.color }}>
        {item.title}
      </div>
    ))}
  </div>
));

interface TotalReviewsCountProps {
  reviewTotalCount: number;
  scrollToReviews: () => void;
}

export const TotalReviewsCount: FC<TotalReviewsCountProps> = memo(
  ({ reviewTotalCount, scrollToReviews }) => (
    <div onClick={scrollToReviews} className='pp-reviews-count'>
      {reviewTotalCount === 0 ? (
        <div>Нет отзывов</div>
      ) : (
        <div>
          {reviewTotalCount}{' '}
          {declension(reviewTotalCount, ['отзыв', 'отзыва', 'отзывов'])}
        </div>
      )}
      <Picture icon={chevronRightDGOutlined} width='20' height='20' />
    </div>
  )
);

interface BestBeforeDateProps {
  bestBeforeDate: string;
  className?: string;
}

export const BestBeforeDate: FC<BestBeforeDateProps> = memo(
  ({ bestBeforeDate, className }) => (
    <div className={twcls('flex items-center text-sm sm:text-xs', className)}>
      <Picture
        pictureClassName={'mr-2 sm:mr-1'}
        icon={calendarBlackFilled}
        width='16'
        height='16'
      />
      Годен до {bestBeforeDate}
    </div>
  )
);

export const DigitalProductDeliveryType: FC = memo(() => (
  <div className='flex items-center mb-4 text-sm sm:text-xs allMd:mb-0'>
    <Picture
      pictureClassName={'mr-2 sm:mr-1'}
      icon={atBlackOutlined}
      width={'16'}
      height='16'
    />
    Доставка на Email
  </div>
));

export const DigitalProductInfoBlock: FC = memo(() => (
  <div className='p-4 bg-[var(--ultra-light-grey)] rounded-xl text-sm md:mt-8 sm:mt-6'>
    <div className='mb-4 font-medium'>Информация о цифровых товарах</div>
    <div className='flex flex-col gap-y-4'>
      {[
        {
          id: 0,
          icon: atBlackOutlined,
          title: 'Отправим на электронную почту'
        },
        {
          id: 0,
          icon: flip2BlackOutlined,
          title: 'Не подлежит обмену на деньги или возврату.'
        }
        // {
        //   id: 0,
        //   icon: infoBlackOutlined,
        //   title: (
        //     <div>
        //       Подробнее об условиях{' '}
        //       <Link to='/' className='underline'>
        //         приобретения
        //       </Link>{' '}
        //       и&nbsp;
        //       <Link to='/' className='underline'>
        //         использования
        //       </Link>{' '}
        //       цифровых товаров
        //     </div>
        //   )
        // }
      ].map(item => (
        <div key={item.id} className='flex items-center gap-x-2'>
          <Picture icon={item.icon} width='16' height='16' /> {item.title}
        </div>
      ))}
    </div>
  </div>
));

export const IsVesovoi: FC = memo(() => (
  <Tooltip
    on={['click', 'hover', 'focus']}
    arrow
    offsetY={0}
    tooltipText={
      'Внимание! Точная стоимость отобразится в Личном кабинете после сборки заказа.'
    }
  >
    <div className='pp-vesovoi'>
      <Picture icon={scaleBlackFilled} width='16' height='16' />
      <span>Весовой</span>
      <Picture icon={qmCircleDGFilled} width='16' height='16' />
    </div>
  </Tooltip>
));

export const IsVesovoiMobile: FC = memo(() => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  return (
    <>
      <div
        className='pp-vesovoi'
        onClick={() => {
          setIsModalOpen(true);
        }}
      >
        <Picture icon={scaleBlackFilled} width='20' height='20' />
        <span>Весовой</span>
        <Picture icon={qmCircleDGFilled} width='16' height='16' />
      </div>
      <InfoModal
        open={isModalOpen}
        onCloseModal={() => {
          setIsModalOpen(false);
        }}
        title='Весовой товар'
        text='Внимание! Точная стоимость отобразится в Личном кабинете после сборки заказа.'
      />
    </>
  );
});

interface BoughtAmountProps {
  count: string | number;
  postfix: string;
}

export const BoughtAmount: FC<BoughtAmountProps> = memo(
  ({ count, postfix }) => (
    <div className='pp-bought'>
      <Picture icon={cartDGFilled} width='16' height='16' />
      <span>
        <span className='sm:hidden'>Купили</span> {count} {postfix}
      </span>
    </div>
  )
);

interface AvailableProps {
  gds_count: number;
  available: boolean;
}

export const Available: FC<AvailableProps> = memo(({ gds_count, available }) =>
  available && gds_count ? (
    gds_count <= 10 ? (
      <div className='pp-available pp-available--count'>
        Осталось {gds_count} шт
      </div>
    ) : null
  ) : null
);

interface DeliveryProps {
  isExpressDeliveryAval: boolean;
  isExpressPickupAval: boolean;
  deliveryDate: string;
}

export const Delivery: FC<DeliveryProps> = memo(
  ({ isExpressPickupAval, deliveryDate, isExpressDeliveryAval }) => (
    <span
      className={clsx(
        `pp-delivery`,
        isExpressPickupAval && 'pp-delivery--orange',
        isExpressDeliveryAval && 'pp-delivery--purple'
      )}
    >
      <Picture
        icon={
          isExpressDeliveryAval
            ? rocketPurpleFilled
            : isExpressPickupAval
            ? carOrangeFilled
            : carBlackFilled
        }
        width='20'
        height='20'
      />
      {isExpressDeliveryAval ? (
        'Доставим от 30 мин'
      ) : isExpressPickupAval ? (
        'Забрать сейчас'
      ) : (
        <span>Доставим&nbsp;{deliveryDate}</span>
      )}
    </span>
  )
);

interface BellProps {
  id: string;
  isDisabledPopup?: boolean;
  isMobile?: boolean;
}

export const Bell: FC<BellProps> = memo(({ id, isDisabledPopup, isMobile }) => {
  const trigger = (
    <div className='flex items-center justify-center w-8 h-8 bg-white rounded-full cursor-pointer'>
      <Picture icon={bellBlackOutlined} width={20} height={20} alt='Bell' />
    </div>
  );
  return (
    <Popover
      on='click'
      disabled={isDisabledPopup}
      trigger={
        <div>
          {isMobile ? (
            trigger
          ) : (
            <Tooltip
              offsetY={0}
              centeredText
              on={['hover', 'focus']}
              tooltipText={'Подписаться на изменение цен товара'}
            >
              {trigger}
            </Tooltip>
          )}
        </div>
      }
      position={isMobile ? 'left center' : 'right center'}
    >
      {close => (
        <SubscribeProduct
          productId={+id}
          title={'Сообщить о скидке'}
          subscriptionType='price_drop'
          closeModal={close}
          withPaddingAndBorderRadius
        />
      )}
    </Popover>
  );
});

interface ArchievedProductProps {
  breadCrumbsLastItem: string;
  changeUrlToUpBreadCrumbs: () => void;
}

export const ArchievedProduct: FC<ArchievedProductProps> = memo(
  ({ breadCrumbsLastItem, changeUrlToUpBreadCrumbs }) => (
    <div className='w-full p-3 bg-[var(--ultra-light-grey)] mb-6 allMd:mb-0 rounded-xl sm:rounded-b-none sm:pb-4'>
      <div className='font-medium text-base mb-2.5'>Поставка не ожидается</div>
      <button
        type='button'
        onClick={changeUrlToUpBreadCrumbs}
        className='allMd:w-full sm:text-sm sm:h-10 cursor-pointer h-[42px] rounded-lg bg-[var(--mid-yellow)] px-10 transition-colors hover:bg-[var(--main-yellow)]'
      >
        {breadCrumbsLastItem}
      </button>
    </div>
  )
);

interface SubscribeProductBlockProps {
  id: number;
  supplyDate?: string;
}

export const SubscribeProductBlock: FC<SubscribeProductBlockProps> = memo(
  ({ id, supplyDate }) => (
    <div className='mb-6 p-3 rounded-xl bg-[var(--light-grey)]'>
      <div className='mb-2 text-base font-bold'>
        Поставка ожидается {supplyDate}
      </div>
      {!!id && <SubscribeProduct productId={id} subscriptionType='in_stock' />}
    </div>
  )
);

export const NotForSaleProduct: FC = memo(() => (
  <div className='pp-not-for-sale'>
    <Link className='pp-not-for-sale__btn' to='/catalog'>
      В каталог
    </Link>
  </div>
));

interface ProductIconsProps {
  isFreeze?: boolean;
  isExpressPickupAval?: boolean;
  isExpressDeliveryAval?: boolean;
  height?: number;
}

export const ProductIcons: FC<ProductIconsProps> = memo(
  ({ isFreeze, isExpressPickupAval, isExpressDeliveryAval, height = 24 }) => {
    if (isFreeze && isExpressPickupAval && isExpressDeliveryAval) {
      return null;
    }
    return (
      <div className='pp-icons'>
        {[
          {
            show: isExpressPickupAval,
            component: <ExpressIcon height={height} />
          },
          {
            show: isExpressDeliveryAval,
            component: <DeliveryFastIcon height={height} />
          },
          {
            show: isFreeze,
            component: <FreezeIcon height={height} />
          }
        ]
          .filter(i => i.show)
          .map((item, index) => (
            <div
              key={index}
              data-num={index}
              style={{
                position: 'absolute',
                zIndex: -index,
                bottom: `${index * 28}px`,
                left: 0
              }}
              className='pp-icons__icon'
            >
              {item.component}
            </div>
          ))}
      </div>
    );
  }
);

interface EmptyTabBannerProps {
  icon: IconType;
  title: string;
  subTitle?: string;
  btnTitle?: string;
  to?: string;
  href?: string;
}

export const EmptyTabBanner: FC<EmptyTabBannerProps> = ({
  icon,
  title,
  subTitle,
  btnTitle,
  to,
  href
}) => (
  <div className='pp-empty-tab-banner'>
    <div className='pp-empty-tab-banner__img'>
      <Picture icon={icon} height='110' width='110' />
    </div>
    <div>
      <div className='pp-empty-tab-banner__title'>{title}</div>
      <div className='pp-empty-tab-banner__sub-title'>{subTitle}</div>
      {href && (
        <a
          href={href}
          className='pp-empty-tab-banner__btn'
          target='_blank'
          rel='noreferrer'
        >
          {btnTitle}
        </a>
      )}
      {to && (
        <Link to={to} className='pp-empty-tab-banner__btn'>
          {btnTitle}
        </Link>
      )}
    </div>
  </div>
);

interface PromocodeGiftItemProps {
  promocodeGiftItems: NonNullable<ProductInfoI>['promocodeGiftItems'];
}

export const PromocodeGiftItem: FC<PromocodeGiftItemProps> = memo(
  ({ promocodeGiftItems }) => {
    const gift = promocodeGiftItems?.find(item => item.type === 'gift');
    if (!gift) {
      return null;
    }
    return (
      <Link
        to={`/product/${gift?.giftItem?.id}`}
        className='bg-[#FEF7D0] p-1 rounded-lg allMd:rounded-none flex items-center'
      >
        <div className='flex-shrink min-w-[2.5rem] w-10 h-10 mr-2 overflow-hidden rounded-lg'>
          <img
            className='object-contain w-full h-full'
            src={gift.giftItem?.fileItem?.cacheItem?.[0].filePath ?? ''}
            alt={`Подарок-${gift?.giftItem?.title}`}
          />
        </div>
        <div className='text-sm'>
          <div className='mb-1 font-medium leading-tight'>Подарок к товару</div>
          <div className='line-clamp-1'>{gift?.giftItem?.title ?? 'Товар'}</div>
        </div>
      </Link>
    );
  }
);

export const ProductPageSocialLinks: FC = memo(() => (
  <div className=''>
    <ShareSocialLinksNew
      offsetYDesktop={0}
      position={'bottom center'}
      trigger={
        <div className='flex items-center justify-center w-8 h-8 bg-white rounded-full cursor-pointer'>
          <HoveredButton
            icon={shareBlackOutlined}
            iconHover={shareRedFilled}
            iconHeight={20}
            iconWidth={20}
          />
        </div>
      }
      link={location.href}
    />
  </div>
));
